import { useCallback, useMemo, useRef, useState } from 'react';

import type { AdminSidebarComponentProps } from '../../../JsonComponent.types';
import type { WeatherReportDaily, WeatherReportContentV103 } from '../WeatherReport.types';
import { useJsonContainerTools } from '../../../hooks/useJsonBlobStorageUtils';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { addError } from '@shared/utils/logger';
import { cloneWeatherReportContent, normalizeWeatherReportContent, createWeatherReportTemplate } from '../WeatherReport.helpers';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { parseOptionalNumber } from '@shared/utils/numbers';
import * as DateFNS from 'date-fns';

export interface UseWeatherReportEditArgs {
  data: AdminSidebarComponentProps['data'];
  eventHandle: AdminSidebarComponentProps['eventHandle'];
}

export interface UseWeatherReportEditResult {
  initialValues: WeatherReportContentV103;
  save: (next: WeatherReportContentV103) => Promise<boolean>;
  toDate: (iso?: string | null) => Date | null;
  toIso: (d?: Date | null) => string;
  mergeDailyWithRange: (existing: WeatherReportDaily[] | undefined, from?: string, to?: string) => WeatherReportDaily[];
  parseNum: (raw: string) => number | undefined;
  isSaving: boolean;
  lastError: unknown;
}

export function useWeatherReportEdit(args: UseWeatherReportEditArgs): UseWeatherReportEditResult {
  const { data, eventHandle } = args;
  const { saveJsonData } = useJsonContainerTools(eventHandle);
  const { updateWebsitePreview } = useUpdateWebsitePreview();

  const fallbackRef = useRef<WeatherReportContentV103>(createWeatherReportTemplate());

  const initialValues = useMemo(() => normalizeWeatherReportContent(data?.content, fallbackRef.current), [data?.content]);

  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState<unknown>(null);

  const toDate = (iso?: string | null): Date | null => {
    return iso ? createDateFromUnformattedString(iso) : null;
  };

  const toIso = (date?: Date | null): string => {
    return date ? DateFNS.format(date, 'yyyy-MM-dd') : '';
  };

  const save = useCallback(
    async (next: WeatherReportContentV103) => {
      setIsSaving(true);
      setLastError(null);

      try {
        const payload = cloneWeatherReportContent(next);
        const { errors } = await saveJsonData({ id: data.id, content: payload });

        if (errors) {
          throw errors;
        }

        updateWebsitePreview();
        return true;
      } catch (error) {
        addError(error);
        setLastError(error);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [data.id, saveJsonData, updateWebsitePreview],
  );

  const datesBetween = (from?: string, to?: string): string[] => {
    const start = toDate(from);
    const end = toDate(to);
    if (!start || !end) return [];

    const out: string[] = [];
    const cursor = new Date(start.getTime());

    while (cursor.getTime() <= end.getTime()) {
      out.push(toIso(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    return out;
  };

  const mergeDailyWithRange = (existing: WeatherReportDaily[] | undefined, from?: string, to?: string): WeatherReportDaily[] => {
    const dates = datesBetween(from, to);
    const map = new Map<string, WeatherReportDaily>();
    (existing || []).forEach(item => map.set(item.date, item));
    return dates.map(date => map.get(date) || { date, summary: '', highTempC: 0, lowTempC: 0 });
  };

  const parseNum = (raw: string): number | undefined => {
    return parseOptionalNumber(raw);
  };

  return { initialValues, save, toDate, toIso, mergeDailyWithRange, parseNum, isSaving, lastError };
}
